import { Model } from 'app/model/backendmodel';
import { AdapterRegistry } from 'app/model/adapters/adapter-registry';
import { IModel } from 'app/model/base/annotation';
import { Metamodel } from 'app/model/base/model';
import { ActivityAsyncAdapter } from 'app/model/adapters/activities/activity.adapter';

import { ValueAdapter } from 'app/model/adapters/value.adapter';
import _ from 'lodash';
import { SetVariableActivity } from 'app/model/pipeline/variables.activity';
import { VariablesConfig } from '../../pipeline/variables-config';
import { Owned } from 'app/model/base/named';
import { Pipeline } from 'app/model/pipeline/pipeline';
import {
    stringsAreEqualCaseInsensitive,
    convertStringToBool,
    convertStringToNumber
} from 'app/shared/utils/string.util';
import { toParameterArray, toParameterClass } from '../linkedservices/parameter-config.util';
import { ISetVariableActivityContext } from 'app/interfaces/copilot.pipeline.action.types';
import { ParameterConfig } from 'shell-models-lib';

@AdapterRegistry.adapter({
    model: Metamodel.modelFor(SetVariableActivity),
    asynchronous: true,
    adapts: {
        name: '*',
        type: '*',
        description: '*',
        policy: '*',
        dependsOn: '*',
        userProperties: '*',
        typeProperties: '*'
    }
})
export class VariablesActivityAdapter extends ActivityAsyncAdapter<
    SetVariableActivity,
    Model.SetVariableActivity,
    ISetVariableActivityContext
> {
    public model(): IModel {
        return Metamodel.modelFor(SetVariableActivity);
    }

    public override async toFabricCopilotContext(activity: SetVariableActivity): Promise<ISetVariableActivityContext> {
        if (activity) {
            const activityContext: ISetVariableActivityContext = await super.toFabricCopilotContext(activity);
            if (activity.setSystemVariable) {
                activityContext.VariableType = 'PipelineReturnValue';
                activityContext.ReturnVariables = (activity.pipelineOutputs ?? []).map((output) => {
                    const variableVal = ValueAdapter.toResource(output, ParameterConfig.VALUE, true);
                    return {
                        Name: output.name,
                        Value: variableVal && typeof variableVal !== 'string' ? JSON.stringify(variableVal) : variableVal,
                        Type: output.type
                    };
                });
            } else if (activity.variables) {
                activityContext.VariableType = 'PipelineVariable';
                const variableVal = ValueAdapter.toResource(activity, SetVariableActivity.VALUE, true);
                activityContext.Variable = {
                    Name: activity.variables.name,
                    Value: variableVal && typeof variableVal !== 'string' ? JSON.stringify(variableVal) : variableVal,
                    Type: activity.variables.type
                };
            }
            return activityContext;
        }
        return undefined;
    }

    public override async fromFabricCopilotContext(
        context: ISetVariableActivityContext,
        model?: SetVariableActivity
    ): Promise<SetVariableActivity> {
        if (context) {
            model = await super.fromFabricCopilotContext(context, model);
            if (context.VariableType === 'PipelineReturnValue') {
                model.setSystemVariable = true;
                model.pipelineOutputs = context.ReturnVariables?.map((output) => {
                    const parameterConfig = Owned.create<ParameterConfig>(ParameterConfig.MODEL, model);
                    parameterConfig.name = output.Name;
                    ValueAdapter.toModel(parameterConfig, ParameterConfig.VALUE, output.Value);
                    parameterConfig.type = output.Type;
                    return parameterConfig;
                });
                const variable = Owned.create<VariablesConfig>(VariablesConfig.MODEL, model);
                variable.name = 'pipelineReturnValue';
                variable.value = [];
                model.variables = variable;
            } else if (context.Variable?.Name) {
                const variable = Owned.create<VariablesConfig>(VariablesConfig.MODEL, model);
                variable.name = context.Variable.Name;
                ValueAdapter.toModel(variable, VariablesConfig.VALUE, context.Variable.Value);
                variable.type = context.Variable.Type ?? 'String';
                model.variables = variable;
            }
        }
        return model;
    }

    protected async _toResource(activity: SetVariableActivity): Promise<Model.SetVariableActivity> {
        const modelVariableActivity: Model.SetVariableActivity = super._getCommonActivityProperties(
            activity,
            'SetVariable'
        ) as Model.SetVariableActivity;
        const expressionValue = ValueAdapter.toResource(activity, SetVariableActivity.MODEL.attribute('value'));
        if (activity?.setSystemVariable) {
            let variableValue = activity?.variables?.value;
            variableValue = toParameterArray(activity.pipelineOutputs);
            modelVariableActivity.typeProperties = {
                variableName: activity.variables.name,
                value: variableValue,
                setSystemVariable: activity.setSystemVariable
            };
        } else if (activity && activity.variables) {
            if (typeof expressionValue === 'object') {
                modelVariableActivity.typeProperties = {
                    variableName: activity.variables.name,
                    value: expressionValue
                };
            } else {
                let rootActivity: Owned = activity;
                while (rootActivity.owner) {
                    rootActivity = rootActivity.owner;
                }

                let variableValue = activity.variables.value;
                if (rootActivity instanceof Pipeline && rootActivity.variables) {
                    const pipelineVar = rootActivity.variables.find(
                        (variable) => variable.name === activity.variables.name
                    );

                    if (pipelineVar) {
                        // Serialize based on the variable type.
                        if (stringsAreEqualCaseInsensitive(pipelineVar._type, 'Boolean')) {
                            const boolVal = convertStringToBool(variableValue as string);

                            // Default to original value if conversion failed.
                            variableValue = boolVal !== undefined ? boolVal : variableValue;
                        } else if (stringsAreEqualCaseInsensitive(pipelineVar._type, 'Integer')) {
                            const intVal = convertStringToNumber(variableValue as string, 'integer');

                            // Default to original value if conversion failed.
                            variableValue = intVal !== undefined ? intVal : variableValue;
                        } else if (stringsAreEqualCaseInsensitive(pipelineVar._type, 'Array')) {
                            try {
                                variableValue = JSON.parse(activity.variables.value);
                            } catch {
                                variableValue = [].concat(
                                    activity.variables.value.split(/,(?![^\(\[]*[\]\)])/).map((val: string) => {
                                        if (val.startsWith('"') && val.endsWith('"')) {
                                            return val.substring(1, val.length - 1);
                                        }
                                        return val;
                                    })
                                );
                            }
                        }
                    }
                }
                modelVariableActivity.typeProperties = {
                    variableName: activity.variables.name,
                    value: variableValue,
                    setSystemVariable: activity.setSystemVariable
                };
            }
        }

        if (activity.raw) {
            const parsed = JSON.parse(activity.raw);
            _.merge(modelVariableActivity, parsed);
        }

        return modelVariableActivity;
    }

    protected async _toModel(modelActivity: Model.SetVariableActivity): Promise<SetVariableActivity> {
        const activity: SetVariableActivity = SetVariableActivity.MODEL.create<SetVariableActivity>(modelActivity.name);
        this._updatedCommonProperties(modelActivity, activity);

        const variableConfig = Owned.create<VariablesConfig>(VariablesConfig.MODEL, activity);
        if (modelActivity.typeProperties) {
            variableConfig.name = modelActivity.typeProperties.variableName;

            const modelValue = modelActivity.typeProperties.value;
            const isModelValueArray = Array.isArray(modelValue);
            let variableValue = '';
            const isSystemVariable = modelActivity.typeProperties.setSystemVariable;
            if (isModelValueArray) {
                variableValue = JSON.stringify(modelValue);
                if (isSystemVariable) {
                    activity.pipelineOutputs = toParameterClass(
                        modelActivity.typeProperties.value,
                        activity,
                        true,
                        true
                    );
                }
            } else if (modelValue !== undefined) {
                variableValue = modelValue.toString();
            } else if (activity.variables) {
                const varObj = this.toVariableObject(activity.variables);
                if (varObj) {
                    variableValue = varObj.value && varObj.value.toString();
                }
            }

            variableConfig.value = variableValue;
            if (isSystemVariable) {
                ValueAdapter.toModel(activity, SetVariableActivity.MODEL.attribute('value'), modelValue);
            } else {
                ValueAdapter.toModel(
                    activity,
                    SetVariableActivity.MODEL.attribute('value'),
                    isModelValueArray ? variableValue : modelValue
                );
            }
            ValueAdapter.toModel(
                activity,
                SetVariableActivity.SETSYSTEMVARIABLE,
                modelActivity.typeProperties.setSystemVariable
            );
        }
        activity.raw = JSON.stringify(
            await ValueAdapter.remainder(this.context, modelActivity, SetVariableActivity.MODEL)
        );
        activity.variables = variableConfig;
        return activity;
    }

    private toVariableObject(varConfig: VariablesConfig): Model.SetVariableActivityTypeProperties {
        return ValueAdapter.toResource(varConfig, SetVariableActivity.MODEL.attribute('variables'));
    }
}
